import { useTranslation } from 'react-i18next';
import { RefObject, useEffect, useId, useRef, useState, useCallback } from 'react';
import styles from './SearchInput.module.css';
import search from '@/assets/icons/search.svg';
import tooltipInfo from '@/assets/icons/tooltip-info.svg';
import CustomTooltip from '@/components/ui/Tooltip/Tooltip';

/*
 * HAT-1212: 88 קליקי זעם על #searchInput ב-14 יום, /new/:id/transactions.
 *
 * הקלט היה מבוקר כולו מה-parent — כל הקשה יצרה re-render של הלוח כולו
 * ושל התוצאות המסוננות; כשיש אלפי שורות זה מרגיש "מת" באמצע ההקלדה,
 * ומשתמש בודק את זה במה שבא ליד — קליק חוזר על התיבה עצמה.
 *
 * מה עשינו:
 *  - state פנימי לאלמנט (draft) שמתעדכן מיד בכל הקשה — התיבה מרגישה חיה;
 *  - עדכון ל-onChange של ה-parent מוזמן ל-DEBOUNCE_MS אחרי ההקשה האחרונה,
 *    כדי שהסינון הכבד ירוץ פעם אחת ולא בכל הקשה;
 *  - כשה-parent מחליף את value מבחוץ (איפוס פילטרים, למשל) — נסנכרן;
 *  - focus נשמר: אנחנו לא מפרקים את ה-input, רק משנים את ה-value שלו.
 */

const DEBOUNCE_MS = 180;

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  tooltipText?: string;
  className?: string;
  inputRef?: RefObject<HTMLInputElement>;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** True while the board is still applying a heavy filter after the debounced value landed. */
  filterPending?: boolean;
}

const SearchInput = ({ value, onChange, placeholder, tooltipText, className, inputRef, onKeyDown, filterPending }: SearchInputProps) => {
  const { t } = useTranslation();
  const tooltipId = useId();
  /*
   * HAT-1212: id="searchInput" ל-<input> ול-<label htmlFor="..."> היה מוצמד
   * קשיח, כלומר שני SearchInput על אותו מסך היו חולקים את אותו id ו-htmlFor
   * היה מפנה תמיד לראשון. useId מספק id יציב לאותו מיפוי, ואם ה-parent רוצה
   * למקד אותו בקוד הוא כבר עובר דרך inputRef.
   */
  const inputId = useId();
  const localInputRef = useRef<HTMLInputElement>(null);
  const mergedInputRef = inputRef ?? localInputRef;

  const [draft, setDraft] = useState(value);
  const [debouncing, setDebouncing] = useState(false);
  /*
   * dirtyRef מבחין בין "המשתמש מקליד" (בגללו יש debounce שרץ) לבין
   * "value השתנה מבחוץ" (איפוס, ניווט חזרה).
   */
  const dirtyRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  /*
   * ⚠️ HAT-1212 (astra): לפני התיקון, dirtyRef בלע את שינוי ה-value מבחוץ
   * לחלוטין — parent שאיפס את הפילטרים תוך כדי הקלדה איבד את האיפוס, וה-timer
   * ה-pending עוד היה יורה את ה-draft הישן ומחזיר את הערך שהמשתמש הקליד.
   * externalValueRef מזכיר לנו מה ה-parent אמר בפעם הקודמת, וכשהוא משתנה
   * — לא משנה אם המשתמש עדיין מקליד — מבטלים את ה-timer, מסנכרנים draft,
   * ומורידים dirty. הפרק היחיד שבו dirty משמש: להחליט אם ההקשה **הזאת**
   * צריכה לייצר debounce.
   */
  const externalValueRef = useRef(value);
  const timerRef = useRef<number | null>(null);

  const cancelPending = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    if (externalValueRef.current === value) return;
    externalValueRef.current = value;
    cancelPending();
    dirtyRef.current = false;
    setDebouncing(false);
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (!dirtyRef.current) {
      setDebouncing(false);
      return;
    }
    setDebouncing(true);
    cancelPending();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      dirtyRef.current = false;
      setDebouncing(false);
      externalValueRef.current = draft;
      onChangeRef.current(draft);
    }, DEBOUNCE_MS);
    return cancelPending;
  }, [draft]);

  const focusField = useCallback(() => {
    mergedInputRef.current?.focus();
  }, [mergedInputRef]);

  const handleContainerPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.closest(`.${styles['tooltip-icon']}`)) return;
      event.preventDefault();
      focusField();
    },
    [focusField]
  );

  const pending = debouncing || Boolean(filterPending);

  return (
    <div
      className={`${styles['search-container']} ${pending ? styles.pending : ''} ${className || ''}`}
      onPointerDown={handleContainerPointerDown}
      data-testid="board-search"
    >
      <label htmlFor={inputId}>
        <img src={search} alt="" />
      </label>
      <input
        id={inputId}
        type="search"
        value={draft}
        onChange={(e) => {
          dirtyRef.current = true;
          setDraft(e.target.value);
        }}
        placeholder={t(placeholder || 'search')}
        className={`${styles['search-input']} body-md-regular text-(--text-body-1)`}
        ref={mergedInputRef}
        onKeyDown={onKeyDown}
        aria-busy={pending || undefined}
      />
      {tooltipText && (
        <>
          <img id={tooltipId} src={tooltipInfo} alt="info" className={styles['tooltip-icon']} tabIndex={0} />
          <CustomTooltip targetId={tooltipId} placement="bottom">
            <div className="body-sm-regular" style={{ color: 'var(--font-body-1)' }}>
              {t(tooltipText)}
            </div>
          </CustomTooltip>
        </>
      )}
    </div>
  );
};

export default SearchInput;
